import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { GridColumnType } from '@/constants/GridColumnType';
import { VisibilityGrid } from '@/constants/VisibilityGrid';

export default interface GridRowCellData {
    type: GridColumnType;
    initials: SourceDataInitials;
    sourceInitial: string;
    sourceInitialIdx?: number | undefined;
    friendlyFieldName: string;
    suggestFieldRule: boolean;
    fieldRulesApplied: boolean;
    dataMatch: boolean;
    dataSelected: boolean;
    fieldNotes: string;
    isFakeData: boolean;
    isPendingSourceData?: boolean;
    value: string;
}

export function normalizeRowVisibilityKey(fieldName: string): string {
    // Reduce a full/indexed field path (ie. "Dwellings-0-Heating-0-DWELL_HEAT") down to its base field name (ie. "DWELL_HEAT").
    const value = String(fieldName ?? '');
    const lastIndex = value.lastIndexOf('-');
    return lastIndex > -1 ? value.slice(lastIndex + 1) : value;
}

const alwaysOnRowVisibilityFields = new Set([
    'DB_UNITNO',
    'DB_CITY',
    'DB_STATE',
    'DB_ZIP',
]);

export function isAlwaysOnRowVisibilityField(fieldName: string): boolean {
    return alwaysOnRowVisibilityFields.has(normalizeRowVisibilityKey(fieldName));
}

export function getRowVisibilityState(
    fieldName: string,
    RowVisibilityGrid: Record<string, string | undefined> | undefined
): VisibilityGrid | 'default' {
    if (isAlwaysOnRowVisibilityField(fieldName)) {
        return VisibilityGrid.AlwaysOn;
    }

    const key = normalizeRowVisibilityKey(fieldName);
    const state = String(RowVisibilityGrid?.[key] ?? '').toLowerCase();

    if (state === VisibilityGrid.AlwaysOn || state === VisibilityGrid.Visible || state === VisibilityGrid.Hidden) {
        return state as VisibilityGrid;
    }

    // No explicit override configured, so callers should fall back to the locally toggled state.
    return 'default';
}

/**
 * Create a new instance with default values.
 * @returns
 */
export function newGridRowCellData(): GridRowCellData {
    const gridRowCellData: GridRowCellData = {
        type: GridColumnType.Unknown,
        initials: SourceDataInitials.None,
        sourceInitial: '',
        sourceInitialIdx: undefined,
        friendlyFieldName: '',
        suggestFieldRule: false,
        fieldRulesApplied: false,
        dataMatch: false,
        dataSelected: false,
        fieldNotes: '',
        isFakeData: false,
        isPendingSourceData: false,
        value: ''
    };
    return gridRowCellData;
}
